import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '@/shared/hooks/use-session';
import { ADMIN_ROUTES, AUTH_ROUTES, DASHBOARD_ROUTES } from '@/api/constants/app.url';
import type { DashboardRole } from '@/shared/types/session';
import {
    activateAccount, readAccounts, type SavedAccount,
} from '@/shared/utils/account-book';

/**
 * The "add account / switch account" hooks behind the topbar profile menu.
 *
 * Reads are synchronous localStorage reads rather than a query: nothing mutates
 * the book without immediately reloading the page, so there is no in-page state
 * to keep in sync and a cache would only add a way for the two to disagree.
 */

/** Sign-in, flagged as adding a second account rather than replacing the first. */
export const ADD_ACCOUNT_PATH = `${AUTH_ROUTES.SIGN_IN}?add=1`;

/** True when the sign-in page was opened by "Add account". */
export function isAddAccountSearch(search: string): boolean {
    return new URLSearchParams(search).get('add') === '1';
}

/** Where a role lands after signing in — admins have their own home. */
export function homePathFor(role: DashboardRole): string {
    return role === 'super_admin' ? ADMIN_ROUTES.INSIGHT : DASHBOARD_ROUTES.HOME;
}

/**
 * Saved accounts OTHER than the one signed in right now — i.e. exactly what the
 * switcher should offer. The active account is already named at the top of the
 * menu, so listing it again would just be a no-op row.
 */
export function useOtherAccounts(): SavedAccount[] {
    const { session } = useSession();
    const currentUserId = session?.userId;
    return useMemo(
        () => readAccounts().filter((a) => a.session.userId !== currentUserId),
        [currentUserId],
    );
}

/**
 * Switch to a saved account: swap the stored credentials, then hard-reload.
 *
 * `location.replace` (not `assign`) so Back can't return to a page rendered for
 * the previous account under its permissions.
 */
export function useSwitchAccount(): (account: SavedAccount) => void {
    return (account: SavedAccount) => {
        activateAccount(account);
        window.location.replace(homePathFor(account.session.role));
    };
}

/**
 * Start adding another account.
 *
 * Routes to the sign-in page flagged as an add, which leaves the current
 * session untouched — abandoning the form leaves the user exactly where they
 * were. Only an EXISTING account can be added: the flagged form drops its
 * "create an account" link.
 */
export function useAddAccount(): () => void {
    const navigate = useNavigate();
    return () => navigate(ADD_ACCOUNT_PATH);
}
